// AudioEngine — Phase 6.
//
// Owns the AudioContext + master chain, a 6-voice pool (poly/mono via the
// VoiceAllocator), the per-osc analyser taps, and the global LFO + mod router.
// `setParam` is the single chokepoint for base values; LFO modulation is summed
// on top via the router, so the two never fight. Param changes broadcast to all
// voices. The full/frozen public surface is consolidated in Phase 8.

import { Voice, Q_RANGE, type OscIndex } from './Voice';
import { VoiceAllocator, POLY_VOICES, type VoiceMode } from './VoiceAllocator';
import { DriveStage } from './Mixer';
import { Sequencer, type SequencerHost } from './Sequencer';
import { LFO, LfoShape } from './modulation/LFO';
import { ModRouter } from './modulation';
import {
  loadOscillatorWorklets,
  supportsAudioWorklet,
  buildSlotConfigs,
} from './oscillators/load';
import {
  getDefaultPatch,
  validatePatch,
  PRESET_VERSION,
  type Patch,
  type Preset,
} from './presets';
import {
  coerceValue,
  getNumericSpec,
  isParamId,
  type ParamId,
  type PatchValue,
} from './params';

/** Thrown by `resume()` when the browser lacks AudioWorklet (per D2). */
export class AudioWorkletUnsupportedError extends Error {
  constructor() {
    super('This browser does not support AudioWorklet.');
    this.name = 'AudioWorkletUnsupportedError';
  }
}

// Hz added to every voice's cutoff per unit of sequencer step filter offset.
const SEQ_FILTER_HZ = 4000;

/** Events the UI can subscribe to. */
export interface EngineEventMap {
  /** A base parameter value changed (id, coerced value). */
  param: (id: ParamId, value: PatchValue) => void;
  /** The sequencer reached a step (index, audio time). */
  step: (index: number, when: number) => void;
}

export class AudioEngine implements SequencerHost {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private masterDrive: DriveStage | null = null;
  private alloc: VoiceAllocator | null = null;
  private lfo: LFO | null = null;
  private router: ModRouter | null = null;
  private analysers: AnalyserNode[] = [];
  private seqFilterOffset: ConstantSourceNode | null = null;
  private seq: Sequencer | null = null;

  private patch: Patch = getDefaultPatch();

  private readonly listeners: {
    [K in keyof EngineEventMap]: Set<EngineEventMap[K]>;
  } = { param: new Set(), step: new Set() };

  get isRunning(): boolean {
    return this.ctx?.state === 'running';
  }

  get context(): AudioContext | null {
    return this.ctx;
  }

  async resume(): Promise<void> {
    if (!this.ctx) await this.init();
    if (this.ctx && this.ctx.state !== 'running') {
      await this.ctx.resume();
    }
  }

  /** Suspend audio (e.g. when the tab is hidden). Safe to call any time. */
  async suspend(): Promise<void> {
    if (this.ctx && this.ctx.state === 'running') {
      await this.ctx.suspend();
    }
  }

  /** Whether the context exists yet (used to gate visibility resume). */
  get isInitialized(): boolean {
    return this.ctx !== null;
  }

  private async init(): Promise<void> {
    const ctx = new AudioContext();
    if (!supportsAudioWorklet(ctx)) {
      await ctx.close();
      throw new AudioWorkletUnsupportedError();
    }
    await loadOscillatorWorklets(ctx);

    const master = ctx.createGain();
    master.gain.value = this.patch['master.volume'] as number;
    const masterDrive = new DriveStage(ctx);
    masterDrive.setDrive(this.patch['master.drive'] as number);

    // Brick-wall-ish safety limiter so a hostile preset / runaway gain can never
    // blast the output: masterDrive -> master -> limiter -> destination.
    const limiter = ctx.createDynamicsCompressor();
    limiter.threshold.value = -2;
    limiter.knee.value = 0;
    limiter.ratio.value = 20;
    limiter.attack.value = 0.003;
    limiter.release.value = 0.1;

    // Final brick wall: a WaveShaper with an identity curve clamps its input to
    // [-1, 1] (the compressor can overshoot), guaranteeing the output never hard
    // clips beyond full scale. Transparent for in-range signals.
    const safety = new DriveStage(ctx); // drive 0 => identity curve
    safety.node.oversample = 'none'; // exact clamp (no interpolation overshoot)

    masterDrive.node.connect(master);
    master.connect(limiter);
    limiter.connect(safety.node);
    safety.node.connect(ctx.destination);

    // One shared analyser per oscillator slot (each voice's osc taps into it).
    const analysers = [0, 1, 2].map(() => {
      const a = ctx.createAnalyser();
      a.fftSize = 2048;
      return a;
    });

    // The wavetable bank is generated once and shared across the pool.
    const slots = buildSlotConfigs();
    const voices: Voice[] = [];
    for (let i = 0; i < POLY_VOICES; i++) {
      voices.push(new Voice(ctx, masterDrive.node, slots, analysers));
    }
    const alloc = new VoiceAllocator(voices);

    // Shared per-step filter offset, summed onto every voice's cutoff.
    const seqFilterOffset = ctx.createConstantSource();
    seqFilterOffset.offset.value = 0;
    for (const v of voices) {
      const cutoff = v.getModTarget('filter.cutoff');
      if (cutoff) seqFilterOffset.connect(cutoff);
    }
    seqFilterOffset.start();
    const seq = new Sequencer(this);
    seq.onStep = (index, when) => this.emit('step', index, when);

    const lfo = new LFO(ctx);
    const router = new ModRouter(
      ctx,
      lfo.output,
      (id) => this.resolveTargets(id),
      (id) => this.scaleForDest(id),
    );

    this.ctx = ctx;
    this.master = master;
    this.masterDrive = masterDrive;
    this.alloc = alloc;
    this.lfo = lfo;
    this.router = router;
    this.analysers = analysers;
    this.seqFilterOffset = seqFilterOffset;
    this.seq = seq;

    lfo.start();
    this.applyPatch(this.patch);
  }

  // --- Note control ---

  noteOn(midi: number): void {
    this.alloc?.noteOn(midi);
  }

  noteOff(midi: number): void {
    this.alloc?.noteOff(midi);
  }

  setVoiceMode(mode: VoiceMode): void {
    this.setParam('voicing.mode', mode === 'mono' ? 1 : 0);
  }

  /** The sequencer (start/stop/setStep/onStep). */
  get sequencer(): Sequencer | null {
    return this.seq;
  }

  // --- SequencerHost: time-scheduled control used by the sequencer ---

  now(): number {
    return this.ctx?.currentTime ?? 0;
  }

  noteOnAt(midi: number, when: number): void {
    this.alloc?.noteOn(midi, when);
  }

  noteOffAt(midi: number, when: number): void {
    this.alloc?.noteOff(midi, when);
  }

  setSeqFilterOffset(norm: number, when: number): void {
    this.seqFilterOffset?.offset.setValueAtTime(norm * SEQ_FILTER_HZ, when);
  }

  // --- Parameter chokepoint ---

  setParam(id: ParamId, value: PatchValue): void {
    const v = coerceValue(id, value);
    this.patch[id] = v;
    this.applyParam(id, v);
    this.emit('param', id, v);
  }

  getParam(id: ParamId): PatchValue {
    return this.patch[id];
  }

  getPatch(): Patch {
    return { ...this.patch };
  }

  /** Apply a full patch (validated/clamped), emitting a `param` event per id. */
  loadPatch(patch: Partial<Patch>): void {
    const valid = validatePatch(patch);
    for (const id of Object.keys(valid) as ParamId[]) {
      this.setParam(id, valid[id]);
    }
  }

  /** Load patch + sequencer grid from a (validated) preset. */
  loadPreset(preset: Preset): void {
    this.loadPatch(preset.patch);
    this.seq?.setState(preset.sequencer);
  }

  /** Snapshot the current patch + sequencer grid as a preset. */
  getPreset(name?: string): Preset {
    return {
      version: PRESET_VERSION,
      ...(name !== undefined ? { name } : {}),
      patch: this.getPatch(),
      sequencer: this.seq?.getState() ?? { steps: [] },
    };
  }

  /** Subscribe to an engine event; returns an unsubscribe function. */
  subscribe<K extends keyof EngineEventMap>(event: K, cb: EngineEventMap[K]): () => void {
    this.listeners[event].add(cb);
    return () => this.listeners[event].delete(cb);
  }

  private emit<K extends keyof EngineEventMap>(
    event: K,
    ...args: Parameters<EngineEventMap[K]>
  ): void {
    for (const cb of this.listeners[event]) {
      (cb as (...a: Parameters<EngineEventMap[K]>) => void)(...args);
    }
  }

  /** Shared analyser for oscillator slot `i` (0..2), for its scope. */
  getAnalyser(i: number): AnalyserNode | null {
    return this.analysers[i] ?? null;
  }

  private applyPatch(patch: Patch): void {
    for (const id of Object.keys(patch) as ParamId[]) {
      this.applyParam(id, patch[id]);
    }
  }

  private applyParam(id: ParamId, value: PatchValue): void {
    const v = value;
    const each = (fn: (voice: Voice) => void): void => this.alloc?.each(fn);

    // Per-oscillator params (osc1/osc2/osc3 . octave|detune|shape|level|drive).
    const osc = /^osc([123])\.(octave|detune|shape|level|drive)$/.exec(id);
    if (osc) {
      const i = (Number(osc[1]) - 1) as OscIndex;
      const n = v as number;
      switch (osc[2]) {
        case 'octave':
          each((vo) => vo.setOctave(i, n));
          break;
        case 'detune':
          each((vo) => vo.setDetune(i, n));
          break;
        case 'shape':
          each((vo) => vo.setShape(i, n));
          break;
        case 'level':
          each((vo) => vo.setLevel(i, n));
          break;
        case 'drive':
          each((vo) => vo.setDrive(i, n));
          break;
      }
      return;
    }

    switch (id) {
      case 'filter.cutoff':
        each((vo) => vo.setCutoff(v as number));
        break;
      case 'filter.resonance':
        each((vo) => vo.setResonance(v as number));
        break;
      case 'filter.envAmount':
        each((vo) => vo.setFilterEnvAmount(v as number));
        break;

      case 'ampEnv.attack':
        each((vo) => vo.setAmpEnv({ attack: v as number }));
        break;
      case 'ampEnv.decay':
        each((vo) => vo.setAmpEnv({ decay: v as number }));
        break;
      case 'ampEnv.sustain':
        each((vo) => vo.setAmpEnv({ sustain: v as number }));
        break;
      case 'ampEnv.release':
        each((vo) => vo.setAmpEnv({ release: v as number }));
        break;

      case 'filterEnv.attack':
        each((vo) => vo.setFilterEnv({ attack: v as number }));
        break;
      case 'filterEnv.decay':
        each((vo) => vo.setFilterEnv({ decay: v as number }));
        break;
      case 'filterEnv.sustain':
        each((vo) => vo.setFilterEnv({ sustain: v as number }));
        break;
      case 'filterEnv.release':
        each((vo) => vo.setFilterEnv({ release: v as number }));
        break;

      case 'lfo.rate':
        this.lfo?.setRate(v as number);
        break;
      case 'lfo.depth':
        this.lfo?.setDepth(v as number);
        break;
      case 'lfo.shape':
        this.lfo?.setShape(v as LfoShape);
        break;
      case 'lfo.dest':
        this.router?.setRoute(v as string);
        break;

      case 'voicing.mode':
        this.alloc?.setMode(v === 1 ? 'mono' : 'poly');
        break;
      case 'voicing.glide':
        this.alloc?.setGlide(v as number);
        break;

      case 'seq.bpm':
        this.seq?.setBpm(v as number);
        break;
      case 'seq.swing':
        this.seq?.setSwing(v as number);
        break;
      case 'seq.steps':
        this.seq?.setStepCount(v as number);
        break;

      case 'master.volume':
        this.setMasterVolume(v as number);
        break;
      case 'master.drive':
        this.masterDrive?.setDrive(v as number);
        break;

      default:
        // Stored in `patch`; audio binding arrives in a later phase.
        break;
    }
  }

  // --- LFO routing convenience (mirrors the Phase 8 API) ---

  setModRoute(destId: string, depth?: number): void {
    if (depth !== undefined) this.setParam('lfo.depth', depth);
    this.setParam('lfo.dest', isParamId(destId) ? destId : 'none');
  }

  clearModRoute(): void {
    this.setParam('lfo.dest', 'none');
  }

  // --- Master ---

  setMasterVolume(value: number): void {
    const spec = getNumericSpec('master.volume');
    const v = Math.min(spec.max, Math.max(spec.min, value));
    this.patch['master.volume'] = v;
    if (this.master && this.ctx) {
      this.master.gain.setTargetAtTime(v, this.ctx.currentTime, 0.01);
    }
  }

  panic(): void {
    if (!this.ctx || !this.master) return;
    const t = this.ctx.currentTime;
    this.alloc?.panic(t);
    this.master.gain.cancelScheduledValues(t);
    this.master.gain.setValueAtTime(0, t);
    this.master.gain.linearRampToValueAtTime(
      this.patch['master.volume'] as number,
      t + 0.05,
    );
  }

  // --- Mod target resolution ---

  private resolveTargets(id: ParamId): AudioParam[] {
    if (id === 'master.volume') {
      return this.master ? [this.master.gain] : [];
    }
    return this.alloc?.getModTargets(id) ?? [];
  }

  private scaleForDest(id: ParamId): number {
    if (id === 'filter.resonance') return Q_RANGE / 2;
    if (id === 'master.volume') return 0.5;
    const spec = getNumericSpec(id);
    return (spec.max - spec.min) / 2;
  }
}
